import { Controller } from "@hotwired/stimulus"
import { showConfirmation } from "../helpers/confirmation_dialog"

export default class FormSubmissionController extends Controller {
  static values = {
    message: {
      type: String,
      default: "Your submission is still in progress. Please wait..."
    },
    guardUnsaved: {
      type: Boolean,
      default: false
    }
  }

  connect() {
    this.submitting = ["/cartoonize", "/comic_projects/"].includes(
      globalThis.location.pathname,
    );
    this.allowHistoryNavigation = false
    this.initialFormState = this.serializeForm()

    this.boundBeforeVisit = this.handleBeforeVisit.bind(this)
    this.boundBeforeUnload = this.handleBeforeUnload.bind(this)
    this.boundPopState = this.handlePopState.bind(this)
    this.boundSubmitEnd = this.handleSubmitEnd.bind(this)

    document.addEventListener(
      "turbo:before-visit",
      this.boundBeforeVisit
    )

    this.element.addEventListener(
      "turbo:submit-start",
      this.start.bind(this)
    )

    this.element.addEventListener(
      "turbo:submit-end",
      this.boundSubmitEnd
    )

    globalThis.addEventListener(
      "beforeunload",
      this.boundBeforeUnload
    )

    globalThis.addEventListener(
      "popstate",
      this.boundPopState,
      true
    )
  }

  disconnect() {
    document.removeEventListener(
      "turbo:before-visit",
      this.boundBeforeVisit
    )

    this.element.removeEventListener(
      "turbo:submit-end",
      this.boundSubmitEnd
    )

    globalThis.removeEventListener(
      "beforeunload",
      this.boundBeforeUnload
    )

    globalThis.removeEventListener(
      "popstate",
      this.boundPopState,
      true
    )

    clearTimeout(this.warningTimeout)
  }

  start() {
    if (this.submitting) {
      return false
    }

    this.submitting = true
    this.allowHistoryNavigation = false

    if (this.guardUnsavedValue) {
      this.allowUnsavedNavigation = true
    }

    this.element.dataset.submitting = "true"

    // this.disableSubmitButtons()

    return true
  }

  cancel() {
    this.submitting = false
    this.allowHistoryNavigation = false

    delete this.element.dataset.submitting

    this.enableSubmitButtons()
    this.removeWarning()
  }

  finish() {
    if (!this.submitting) {
      return
    }

    this.submitting = false
    this.allowHistoryNavigation = true

    delete this.element.dataset.submitting

    this.enableSubmitButtons()
    this.removeWarning()
  }

  handlePopState(event) {
    const submissionInProgress =
      this.submitting &&
      !this.allowHistoryNavigation &&
      !this.guardUnsavedValue

    const unfinishedPost =
      this.guardUnsavedValue &&
      !this.allowUnsavedNavigation &&
      this.hasUnsavedChanges()

    if (!submissionInProgress && !unfinishedPost) {
      return
    }

    event.preventDefault()
    event.returnValue = ""
  }

  async handleBeforeVisit(event) {
    if (
      this.submitting &&
      !this.allowHistoryNavigation &&
      !this.guardUnsavedValue
    ) {
      event.preventDefault()
      this.showWarning()
      return
    }

    if (
      !this.guardUnsavedValue ||
      this.allowUnsavedNavigation ||
      !this.hasUnsavedChanges()
    ) {
      return
    }

    event.preventDefault()
    if (this.navigationConfirmationOpen) return

    this.navigationConfirmationOpen = true
    const confirmed = await showConfirmation({
      title: "Discard post?",
      text: "Discard your unfinished post?",
      confirmButtonText: "Discard",
      cancelButtonText: "Keep Editing"
    })
    this.navigationConfirmationOpen = false

    if (!confirmed) return

    this.allowUnsavedNavigation = true
    await this.discardAndVisit(event.detail.url, event.detail.action)
  }

  async discardAndVisit(url, action) {
    const stripPostController =
      this.application.getControllerForElementAndIdentifier(
        this.element,
        "strip-post"
      )

    if (stripPostController?.resetPostForm) {
      await stripPostController.resetPostForm()
    } else {
      this.element.reset()
    }

    this.initialFormState = this.serializeForm()

    Turbo.visit(url, { action: action || "advance" })
  }

  handleBeforeUnload(event) {
    const submissionInProgress =
      this.submitting &&
      !this.allowHistoryNavigation &&
      !this.guardUnsavedValue

    const unfinishedPost =
      this.guardUnsavedValue &&
      !this.allowUnsavedNavigation &&
      this.hasUnsavedChanges()

    if (!submissionInProgress && !unfinishedPost) {
      return
    }

    event.preventDefault()
    event.returnValue = ""
  }

  hasUnsavedChanges() {
    const hasMedia = this.element.querySelector(
      ".filepond--item"
    ) !== null

    const hasEditorContent = Array.from(
      this.element.querySelectorAll(".ql-editor")
    ).some(editor => editor.textContent.trim().length > 0)

    return hasMedia ||
      hasEditorContent ||
      this.serializeForm() !== this.initialFormState
  }

  markClean() {
    this.submitting = false
    this.allowHistoryNavigation = false
    this.allowUnsavedNavigation = false
    this.initialFormState = this.serializeForm()

    delete this.element.dataset.submitting
    this.removeWarning()
  }

  serializeForm() {
    if (this.element.tagName !== "FORM") return [];

    const ignoredFields = new Set([
      "authenticity_token",
      "utf8",
      "_method"
    ])

    this.element
      .querySelectorAll("[data-controller~='quill'][data-name]")
      .forEach(editor => ignoredFields.add(editor.dataset.name))

    return JSON.stringify(
      Array.from(new FormData(this.element).entries())
        .filter(([name, value]) => {
          if (ignoredFields.has(name)) return false

          return !(value instanceof File) ||
            value.name.length > 0 ||
            value.size > 0
        })
        .map(([name, value]) => {
          if (!(value instanceof File)) return [name, value]

          return [
            name,
            value.name,
            value.size,
            value.type,
            value.lastModified
          ]
        })
    )
  }

  handleSubmitEnd(event) {
    if (event.target !== this.element) {
      return
    }

    if (event.detail.success) {
      this.finish()
    } else {
      this.cancel()
      this.allowUnsavedNavigation = false
    }
  }

  // disableSubmitButtons() {
  //   this.submitButtons = Array.from(
  //     this.element.querySelectorAll(
  //       'button[type="submit"], input[type="submit"]'
  //     )
  //   )

  //   this.submitButtons.forEach((button) => {
  //     button.dataset.formSubmissionDisabled =
  //       button.disabled ? "true" : "false"

  //     button.disabled = true
  //   })
  // }

  enableSubmitButtons() {
    if (!this.submitButtons) {
      return
    }

    this.submitButtons.forEach((button) => {
      button.disabled =
        button.dataset.formSubmissionDisabled === "true"

      delete button.dataset.formSubmissionDisabled
    })

    this.submitButtons = null
  }

  showWarning() {
    if (document.getElementById("form-submission-warning")) {
      return
    }

    const warning = document.createElement("div")

    warning.id = "form-submission-warning"

    warning.className =
      "position-fixed bottom-0 start-50 translate-middle-x " +
      "mb-4 bg-dark text-white px-4 py-3 rounded-3 " +
      "shadow-lg z-9999"

    warning.textContent = this.messageValue

    document.body.appendChild(warning)

    clearTimeout(this.warningTimeout)

    this.warningTimeout = setTimeout(() => {
      this.removeWarning()
    }, 4000)
  }

  removeWarning() {
    clearTimeout(this.warningTimeout)

    document
      .getElementById("form-submission-warning")
      ?.remove()
  }
}
